/**
 * Nursery accounts and receivables: head offices and their sites, invoices
 * as documents, payments, credit notes, rebates and the org's own Xero.
 *
 * Plan: docs/nursery-accounts-receivable-plan.md.
 * Backend: HHNodeServer routes/nurseryReceivables.js.
 *
 * Money arrives as numbers (the server's pg parser turns NUMERIC into floats),
 * but an older row or a JSON aggregate can carry a string, so display goes
 * through `money()` in utils/nurseryMoney.ts rather than trusting the type.
 */

type Num = number | string;

// ─── Accounts ───

export type PaymentTermsType = 'days' | 'eom_days';
export type InvoiceDelivery = 'email' | 'portal' | 'none';
export type TermsSource = 'own' | 'parent' | 'default';

export interface PaymentTerms {
  type: PaymentTermsType;
  days: number;
  source: TermsSource;
}

/** What a customer trades on once its head office's settings are counted. */
export interface CustomerAccount {
  customer_id: number;
  customer_name: string;
  parent_id: number | null;
  parent_name: string | null;
  payer_id: number;
  payer_name: string;
  pays_for_itself: boolean;
  customer_tier_id: number | null;
  terms: PaymentTerms | null;
  po_required: boolean;
  credit_limit: number | null;
  invoice_delivery: InvoiceDelivery;
  /** The farm its orders leave from: its own, else the head office's. */
  despatch_farm_id?: number | null;
  despatch_farm_name?: string | null;
}

export interface CreditPosition {
  payer_id: number;
  payer_name: string;
  balance: number;
  credit_limit: number | null;
  over_limit: boolean;
  /** Owed for more than 30 days past due. */
  overdue_30: number;
}

export interface CustomerSite {
  id: number;
  name: string;
  customer_code: string | null;
  bills_to_parent: boolean;
  active: boolean;
  delivery_address: string | null;
  balance: number;
}

/**
 * Which list priced a line: the customer's own, its head office's, or the
 * default. `tier`, `base` and `website` came from before 2026-10-12 and only
 * appear on quotes from an older server.
 */
export type PriceSource = 'customer' | 'parent' | 'default' | 'tier' | 'base' | 'website';

export interface PriceQuote {
  unit_price: number | null;
  source?: PriceSource;
  source_label?: string;
  price_list_id?: number | null;
  price_list_name?: string | null;
}

/** A list as the Prices tab shows it, in force today or not. */
export interface CustomerPriceListSummary {
  id: number;
  name: string;
  status: 'draft' | 'active' | 'archived';
  effective_from: string;
  effective_to: string | null;
  is_default: boolean;
  in_force: boolean;
  item_count: number;
}

/** The one list a customer is priced from, and why. */
export interface CustomerPriceList {
  /** Chosen for this customer; null when it takes its head office's or the default. */
  price_list_id: number | null;
  parent_price_list_id: number | null;
  parent_name: string | null;
  /** The list that prices it, or null when there is none. */
  list: CustomerPriceListSummary | null;
  source: 'customer' | 'parent' | 'default' | null;
  source_label: string | null;
  default_list: CustomerPriceListSummary | null;
}

export interface PriceListInForce {
  id: number;
  name: string;
  effective_from: string;
  effective_to: string | null;
  customer_id: number | null;
  customer_tier_id: number | null;
  tier_name: string | null;
  item_count: number;
  source: PriceSource;
  source_label: string;
}

// ─── Invoices ───

export type InvoiceStatus = 'draft' | 'sent' | 'paid' | 'overdue' | 'cancelled';
/** Derived from the balance and the due date; never stored. */
export type PaymentState = 'draft' | 'open' | 'part_paid' | 'overdue' | 'paid' | 'void';
export type XeroSyncStatus = 'pending' | 'pushing' | 'synced' | 'failed' | null;

export interface InvoiceLine {
  id: number;
  kind: 'goods' | 'delivery';
  description: string;
  quantity: Num;
  unit_price: Num;
  discount_pct: Num;
  line_total: Num;
  gst_amount: Num;
  /** The retailer's own code for the line, from its purchase order. */
  retailer_item_number?: string | null;
  retailer_barcode?: string | null;
}

/**
 * One line of the invoice's order: what was ordered against what this
 * invoice bills. A retailer matches the invoice to its PO line by line, and
 * a line not picked at all is not on the invoice.
 */
export interface InvoiceOrderLine {
  order_line_id: number;
  description: string;
  retailer_item_number: string | null;
  retailer_barcode: string | null;
  quantity_ordered: number;
  quantity_invoiced: number;
  quantity_short: number;
  /** The backorder the rest went to, if any. */
  backorder_number: string | null;
}

export interface InvoiceRow {
  id: number;
  order_id: number;
  customer_id: number | null;
  invoice_number: string;
  invoice_date: string;
  due_date: string | null;
  subtotal: Num;
  gst_total: Num;
  total_amount: Num;
  amount_paid: Num;
  amount_credited: Num;
  balance: number;
  status: InvoiceStatus;
  payment_state: PaymentState;
  authorised_at: string | null;
  notes: string | null;
  dispute_status: 'open' | 'resolved' | null;
  dispute_note: string | null;
  xero_invoice_id: string | null;
  xero_sync_status: XeroSyncStatus;
  xero_sync_error: string | null;
  xero_status: string | null;
  customer_name: string | null;
  customer_code: string | null;
  parent_customer_id: number | null;
  parent_name: string | null;
  payer_id: number | null;
  payer_name: string | null;
  order_number: string;
  customer_po: string | null;
  order_delivery_address: string | null;
}

export type CreditReason =
  | 'short_delivery' | 'damaged' | 'price_correction' | 'rebate'
  | 'settlement_discount' | 'short_payment' | 'other';

export const CREDIT_REASON_LABELS: Record<CreditReason, string> = {
  short_delivery: 'Short delivery',
  damaged: 'Damaged or dead on arrival',
  price_correction: 'Price correction',
  rebate: 'Rebate',
  settlement_discount: 'Settlement discount',
  short_payment: 'Short payment accepted',
  other: 'Other',
};

/** The reasons a person may pick. A rebate is raised by its agreement. */
export const MANUAL_CREDIT_REASONS: CreditReason[] = [
  'short_delivery', 'damaged', 'price_correction', 'settlement_discount', 'short_payment', 'other',
];

export interface CreditNoteLine {
  id: number;
  description: string;
  line_total: Num;
  gst_amount: Num;
  account_code: string | null;
}

export interface CreditNote {
  id: number;
  customer_id: number | null;
  invoice_id: number | null;
  credit_note_number: string;
  credit_date: string;
  reason: CreditReason;
  subtotal: Num;
  gst_total: Num;
  total_amount: Num;
  status: 'authorised' | 'void';
  notes: string | null;
  void_reason: string | null;
  rebate_agreement_id: number | null;
  rebate_agreement_name?: string | null;
  xero_credit_note_id: string | null;
  xero_sync_status: XeroSyncStatus;
  xero_sync_error: string | null;
  invoice_number?: string | null;
  customer_name?: string | null;
  customer_code?: string | null;
  lines?: CreditNoteLine[];
}

export type PaymentMethod = 'eft' | 'card' | 'cash' | 'cheque' | 'stripe' | 'other';
export type PaymentSource = 'manual' | 'xero' | 'stripe' | 'legacy' | 'counter' | 'remittance';

export const PAYMENT_METHOD_LABELS: Record<PaymentMethod, string> = {
  eft: 'Bank transfer', card: 'Card', cash: 'Cash', cheque: 'Cheque', stripe: 'Paid online', other: 'Other',
};

export interface InvoicePayment {
  id: number;
  payment_date: string;
  method: PaymentMethod;
  reference: string | null;
  source: PaymentSource;
  status: 'active' | 'void';
  allocated: Num;
  payment_amount: Num;
  xero_payment_id?: string | null;
  xero_push_error?: string | null;
}

export interface InvoiceDetail extends InvoiceRow {
  lines: InvoiceLine[];
  /** A draft's lines whose order line has no price. It cannot be authorised until they do. */
  unpriced_lines?: { id: number; description: string; quantity: Num }[];
  /** Absent from a server older than the comparison. */
  order_lines?: InvoiceOrderLine[];
  credit_notes: CreditNote[];
  payments: InvoicePayment[];
  account: {
    payer_id: number;
    payer_name: string;
    invoice_delivery: InvoiceDelivery;
    terms: PaymentTerms | null;
  } | null;
}

/**
 * A credit line is chosen from the invoice (Rob, 2026-10-13): trees by count,
 * or an amount against a line (the delivery, a price correction). The server
 * writes the description and works out the amount and GST.
 */
export type ChosenCreditLine =
  | { invoice_line_id: number; quantity: number }
  | { invoice_line_id: number; amount: number };

/** An invoice line and what is still left to credit on it. */
export interface CreditableLine {
  id: number;
  kind: 'goods' | 'delivery';
  description: string;
  stock_item_id: number | null;
  product_id: number | null;
  quantity: number;
  /** Ex GST. */
  line_total: number;
  /** Ex GST, per tree on a goods line. */
  unit_amount: number;
  gst: boolean;
  credited_quantity: number;
  credited_amount: number;
  /** Null on the delivery, which is credited as an amount. */
  creditable_quantity: number | null;
  creditable_amount: number;
}

export interface CreateCreditNotePayload {
  invoice_id: number;
  reason: CreditReason;
  credit_date?: string;
  notes?: string;
  lines: ChosenCreditLine[];
}

/**
 * A credit note asked for by a nursery manager, waiting for an org admin or an
 * Accounts Admin (HHNodeServer nurseryCreditRequestService).
 */
export type CreditRequestStatus = 'pending' | 'approved' | 'declined' | 'withdrawn';

export interface CreditNoteRequest {
  id: number;
  invoice_id: number;
  invoice_number: string;
  invoice_status: string;
  order_id: number | null;
  order_number: string | null;
  customer_name: string | null;
  reason: CreditReason;
  explanation: string;
  /** line_total is ex GST. */
  lines: { description: string; line_total: number; gst: boolean; gst_amount: number }[];
  subtotal: Num;
  gst_total: Num;
  total_amount: Num;
  status: CreditRequestStatus;
  requested_by_user_id: number | null;
  requested_by_name: string | null;
  requested_at: string;
  decided_by_name: string | null;
  decided_at: string | null;
  decision_note: string | null;
  credit_note_id: number | null;
  credit_note_number: string | null;
}

export interface CreditRequestAccess {
  can_approve: boolean;
  can_request: boolean;
  /** Requests waiting in the organisation. */
  pending: number;
}

export interface CreateCreditRequestPayload {
  invoice_id: number;
  reason: CreditReason;
  explanation: string;
  lines: ChosenCreditLine[];
}

/** Mirrors HHNodeServer nurseryCreditRequestService.explanationProblem. */
export const MIN_EXPLANATION_CHARS = 20;
export const MIN_EXPLANATION_WORDS = 4;

export function explanationIsEnough(text: string): boolean {
  const t = text.trim();
  const words = t.split(/\s+/).filter((w) => /[A-Za-zÀ-ɏ]{2,}/.test(w));
  return t.length >= MIN_EXPLANATION_CHARS && words.length >= MIN_EXPLANATION_WORDS;
}

export interface Payment {
  id: number;
  payer_customer_id: number | null;
  payer_name: string | null;
  payment_date: string;
  amount: Num;
  method: PaymentMethod;
  reference: string | null;
  source: PaymentSource;
  status: 'active' | 'void';
  void_reason: string | null;
  notes: string | null;
  allocations: { invoice_id: number; invoice_number: string; amount: Num }[];
  unallocated: number;
}

export interface RecordPaymentPayload {
  payer_customer_id: number;
  payment_date?: string;
  amount: number;
  method?: Exclude<PaymentMethod, 'stripe'>;
  reference?: string;
  notes?: string;
  /** Omit to spread it oldest due first. */
  allocations?: { invoice_id: number; amount: number }[];
}

// ─── Receivables reports ───

export interface Ageing {
  current: number;
  d1_30: number;
  d31_60: number;
  d61_90: number;
  d90_plus: number;
  total: number;
}

export interface AgedSite extends Ageing {
  customer_id: number | null;
  customer_name: string;
  customer_code: string | null;
}

export interface AgedPayer extends Ageing {
  key: string;
  payer_id: number | null;
  payer_name: string;
  credit_limit: number | null;
  over_limit: boolean;
  /** Balance on invoices with an open dispute. */
  disputed: number;
  invoices: number;
  /** Credit not yet set against an invoice (a period-close rebate, an overpayment). */
  on_account: number;
  net: number;
  sites: AgedSite[];
}

export interface AgedDebtors {
  as_at: string;
  totals: Ageing;
  payers: AgedPayer[];
}

export interface ExpectedInvoice {
  id: number;
  invoice_number: string;
  customer_name: string | null;
  customer_code: string | null;
  customer_po: string | null;
  invoice_date: string;
  due_date: string | null;
  total_amount: Num;
  amount_credited: Num;
  balance: number;
  payment_state: PaymentState;
  dispute_status: 'open' | 'resolved' | null;
}

export interface ExpectedPayer {
  payer_id: number | null;
  payer_name: string;
  total: number;
  overdue: number;
  invoices: ExpectedInvoice[];
  settlement_discount: { rate_pct: number; days: number } | null;
}

export interface ExpectedPayments {
  by: string;
  payers: ExpectedPayer[];
}

export interface ShortPay extends InvoiceRow {
  days_overdue: number;
}

export interface StatementLine {
  kind: 'invoice' | 'credit_note' | 'payment';
  id: number;
  date: string;
  number: string | null;
  site: string | null;
  customer_code: string | null;
  reference: string | null;
  due_date: string | null;
  debit: number;
  credit: number;
  balance: number;
}

export interface Statement {
  payer: { id: number; name: string; customer_code: string | null };
  from: string;
  to: string;
  opening_balance: number;
  closing_balance: number;
  lines: StatementLine[];
  ageing: Ageing;
}

// ─── Rebates ───

export type RebateKind = 'flat_pct' | 'volume_tiered' | 'fixed_amount' | 'settlement_discount';
export type RebateSettlement = 'per_invoice' | 'credit_note' | 'billed_to_us';
export type RebatePeriod = 'month' | 'quarter' | 'year';
export type RebateBandMode = 'retrospective' | 'marginal';

export const REBATE_KIND_LABELS: Record<RebateKind, string> = {
  flat_pct: 'A percentage of everything invoiced',
  volume_tiered: 'A percentage that rises with spend',
  fixed_amount: 'A fixed amount each period',
  settlement_discount: 'A discount for paying early',
};

export const REBATE_SETTLEMENT_LABELS: Record<RebateSettlement, string> = {
  per_invoice: 'Taken off each payment',
  credit_note: 'Credited at the end of the period',
  billed_to_us: 'They bill us for it',
};

export interface RebateComponent {
  id?: number;
  label: string;
  rate_pct: Num | null;
  fixed_amount: Num | null;
  account_code: string | null;
}

export interface RebateBand {
  id?: number;
  threshold: Num;
  rate_pct: Num;
}

export interface RebateStatementSummary {
  id: number;
  period_start: string;
  period_end: string;
  spend: Num;
  rate_pct: Num | null;
  earned: Num;
  already_credited: Num;
  balance: Num;
  content_hash: string;
  closed_at: string;
  credit_note_id: number | null;
  credit_note_number: string | null;
}

export interface RebateAgreement {
  id: number;
  customer_id: number;
  customer_name: string;
  customer_code: string | null;
  name: string;
  kind: RebateKind;
  settlement: RebateSettlement;
  period: RebatePeriod;
  period_start_month: number;
  band_mode: RebateBandMode;
  include_delivery: boolean;
  discount_days: number | null;
  account_code: string | null;
  effective_from: string;
  effective_to: string | null;
  notes: string | null;
  revoked_at: string | null;
  revoke_reason: string | null;
  components: RebateComponent[];
  bands: RebateBand[];
  total_rate_pct: number;
  statements?: RebateStatementSummary[];
}

export interface CreateRebateAgreementPayload {
  customer_id: number;
  name: string;
  kind: RebateKind;
  settlement?: RebateSettlement;
  period?: RebatePeriod;
  period_start_month?: number;
  band_mode?: RebateBandMode;
  include_delivery?: boolean;
  discount_days?: number | null;
  account_code?: string | null;
  effective_from: string;
  effective_to?: string | null;
  notes?: string;
  components: { label: string; rate_pct?: number | null; fixed_amount?: number | null; account_code?: string | null }[];
  bands: { threshold: number; rate_pct: number }[];
}

export interface RebateAccrual {
  agreement_id: number;
  kind: RebateKind;
  settlement: RebateSettlement;
  period: { start: string; end: string };
  /** The period is still running. */
  running: boolean;
  /** Ended, not yet closed. */
  closable: boolean;
  spend: number;
  invoices: number;
  by_site: { site_id: number; site_name: string; customer_code: string | null; invoices: number; spend: number }[];
  by_month: { month: string; spend: number }[];
  rate_pct: number | null;
  band: { threshold: number; rate_pct: number } | null;
  next_band: { threshold: number; rate_pct: number; spend_to_reach: number } | null;
  earned: number;
  already_credited: number;
  balance: number;
  projected_spend: number;
  projected_earned: number;
  statement: RebateStatementSummary | null;
}

// ─── Refunds ───

export interface OrderRefund {
  id: number;
  order_id: number;
  amount: Num;
  /** `cash_due`: paid in cash at the counter, waiting to be handed back from a till. */
  status: 'pending' | 'succeeded' | 'failed' | 'cash_due';
  /** Absent from an older server, where every refund was to a card. */
  method?: 'card' | 'cash';
  stripe_refund_id: string | null;
  error: string | null;
  completed_at: string | null;
}

// ─── Xero Accounting ───

export interface XeroAccountingMapping {
  sales_account_code: string | null;
  delivery_account_code: string | null;
  rebate_account_code: string | null;
  credit_account_code: string | null;
  gst_tax_type: string | null;
  /** Where card payments taken at the counter land (a Stripe clearing account). */
  card_clearing_account_code?: string | null;
  /** Where cash taken at the counter lands. */
  cash_account_code?: string | null;
  /** Xero bank account that receives cash when a till deposit is banked. */
  cash_deposit_account_code?: string | null;
}

export interface XeroAccountingStatus {
  /** False on a test server: Xero only runs in production. */
  enabled_here: boolean;
  /** Connected for invoicing: the Xero sign-in exists and holds the accounting permissions. */
  connected: boolean;
  /** The Xero sign-in exists, whatever it can reach. */
  xero_connected?: boolean;
  tenant_selected: boolean;
  tenant_id: string | null;
  tenant_name: string | null;
  xero_user_name: string | null;
  connected_at: string | null;
  mapping: XeroAccountingMapping;
  /** What the mapping still lacks, in words ("a rebate account"). */
  mapping_missing: string[];
  last_sync_at: string | null;
  last_sync_error: string | null;
  queue: {
    invoices_pending: number;
    invoices_failed: number;
    credit_notes_pending: number;
    credit_notes_failed: number;
    /** Counter payments Xero has not taken (no account chosen, or refused). */
    counter_payments_failed?: number;
  };
}

export interface XeroAccount {
  code: string;
  name: string;
  type: string;
  class: string;
  tax_type: string | null;
  /** A bank account, or one marked for payments: the only kind Xero takes a payment into. */
  takes_payments?: boolean;
}

export interface XeroTaxRate {
  tax_type: string;
  name: string;
  rate: number;
}

/** A contact in the nursery's Xero, as the customer page's picker shows it. */
export interface XeroContactSummary {
  contact_id: string;
  name: string;
  account_number: string | null;
  email: string | null;
  tax_number: string | null;
  archived: boolean;
}

/** Which Xero contact a customer's invoices go to. */
export interface CustomerXeroContact {
  /** Xero is connected, an organisation chosen, and this is the live system. */
  xero_active: boolean;
  /** Set for a site billed to its head office: the contact is the head office's. */
  bills_to: { id: number; name: string } | null;
  /** The account number a contact we create is given (`NC<id>`). */
  account_number: string;
  contact_id: string | null;
  contact: XeroContactSummary | null;
  /** Why the linked contact could not be read from Xero. */
  contact_error: string | null;
}

export interface XeroFailures {
  invoices: { id: number; invoice_number: string; total_amount: Num; xero_sync_error: string | null; customer_name: string }[];
  credit_notes: { id: number; credit_note_number: string; total_amount: Num; xero_sync_error: string | null; customer_name: string }[];
}

/** The organisation's terms of trade with its customers (`/nursery/trading-terms`). */
export interface TradingTerms {
  /** Printed above them: "Terms of trade", "Conditions of Sale". */
  heading: string;
  terms: string;
  on_invoices: boolean;
  on_statements: boolean;
  on_credit_notes: boolean;
  updated_at: string | null;
  updated_by_name: string | null;
}

export type SaveTradingTermsPayload = Pick<TradingTerms, 'heading' | 'terms' | 'on_invoices' | 'on_statements' | 'on_credit_notes'>;

/** Mirrors HHNodeServer nurseryTradingTermsService.MAX_LENGTH. */
export const TRADING_TERMS_MAX_LENGTH = 30000;
